<script>
  import { onMount } from 'svelte';
  import { selectedItem } from '../store/store.js';
  import { goto } from '$app/navigation'; // Import goto for navigation
  import { generateStars } from '../utils/utils.js';
  
  
  let mapElement;
  let map;
  let marker;
  let nearbyMarkersLayer = null; // To manage nearby markers easily
  
  // Track when selectedItem changes to reinitialize the map
  $: if ($selectedItem && mapElement) {
    initializeMap();
  }
  
  // --- Define the main icon once ---
  const customIcon = L.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="35" height="35">
        <path fill="#ec776a" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    `),
    iconSize: [35, 35],
    iconAnchor: [17, 35],
    popupAnchor: [1, -34],
    shadowUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 41 41" width="41" height="41">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="1" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/>
          </filter>
        </defs>
        <ellipse cx="20.5" cy="38.5" rx="10" ry="2" fill="#000000" opacity="0.3" filter="url(#shadow)"/>
      </svg>
    `),
    shadowSize: [41, 41],
    shadowAnchor: [12, 41]
  });
  
  // Helper function to get coordinates from the selected item
  function getCoordinates() {
    if ($selectedItem && $selectedItem.latitude && $selectedItem.longitude) {
      return {
        latitude: $selectedItem.latitude,
        longitude: $selectedItem.longitude,
        address: $selectedItem.addresses && $selectedItem.addresses.length > 0 ? $selectedItem.addresses[0] : null
      };
    }
    return null;
  }
  
  // Helper function to convert names to URL-friendly slugs
  function slugify(text) {
    if (!text) return '';
    return text.toString().toLowerCase()
      .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
      .replace(/[^\w\-]+/g, '-').replace(/\-\-+/g, '-')
      .replace(/^-+/, '').replace(/-+$/, '');
  }
  
  // Function to fetch and display nearby BRFs
  async function fetchAndDisplayNearbyBrfs(lat, lon, currentBrfCoreId) {
    const limit = 20;
    const backendUrl = `https://boanalys-backend-324523837899.europe-north1.run.app/nearest_brfs?latitude=${lat}&longitude=${lon}&limit=${limit}`;
    console.log('Fetching nearby BRFs for core_id:', currentBrfCoreId); // Log the ID being excluded

    try {
      console.log(`Fetching nearby BRFs from: ${backendUrl}`);
      const response = await fetch(backendUrl);
      console.log('Fetch response status:', response.status); // Log status

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
            // Try to get more details from the response body (might be HTML or JSON)
            const errorBody = await response.text(); // Read as text first
            console.error("Error response body:", errorBody);
            try {
                const errorData = JSON.parse(errorBody); // Try parsing as JSON
                errorMsg += `, Message: ${errorData.detail || errorData.message || response.statusText}`;
            } catch (parseError) {
                 errorMsg += `, Body: ${errorBody.substring(0, 100)}...`; // Show beginning of non-JSON body
            }
        } catch (e) { /* Ignore if reading body fails */ }
        throw new Error(errorMsg);
      }
      
      // --- Adjust parsing based on the actual response structure ---
      const responseData = await response.json(); // Expecting JSON now
      console.log('Received response data:', responseData); // Log the raw response
      
      // Check if the response status is 'success' and data exists
      if (responseData.status !== 'success' || !responseData.data) {
          console.warn("Nearby BRF fetch did not return success or data:", responseData);
          if (nearbyMarkersLayer) {
              map.removeLayer(nearbyMarkersLayer);
              nearbyMarkersLayer = null;
          }
          return; // Exit the function
      }

      // --- Filter out the current BRF using core_id ---
      const allNearbyBrfs = responseData.data;
      console.log('All nearby BRFs received from API:', allNearbyBrfs); // Log before filtering
      const nearbyBrfs = allNearbyBrfs.filter(brf => brf.core_id !== currentBrfCoreId);
      console.log(`Found ${nearbyBrfs.length} unique nearby BRFs (excluding current):`, nearbyBrfs);
      // -------------------------------------------------

      if (map && nearbyBrfs && nearbyBrfs.length > 0) {
        if (nearbyMarkersLayer) {
          map.removeLayer(nearbyMarkersLayer);
        }
        const markers = [];
        console.log('Processing nearby BRFs to create markers...'); // Log before loop

        nearbyBrfs.forEach((brf, index) => {
          console.log(`Processing BRF #${index}:`, brf); // Log each BRF object
          if (brf && brf.latitude && brf.longitude && brf.core_id && brf.brf_name) {
            const title = brf.brf_name;

            // --- Generate the star HTML block using the imported function ---
            let starsHtmlBlock = ''; // Default to empty string
            if (brf.overall_rating && typeof brf.overall_rating.rating === 'number') {
              starsHtmlBlock = generateStars(brf.overall_rating.rating); // Generate the HTML block
              console.log('Generated stars HTML:', starsHtmlBlock); // Debug the generated HTML
            } else {
              console.log('No rating available for:', title, brf.overall_rating);
              // Add a fallback for when no rating is available
              starsHtmlBlock = '<div class="rating-stars"><span class="stars-na">Ej betygsatt</span></div>';
            }

            // Create a unique ID for this marker's button
            const buttonId = `nearby-brf-button-${brf.core_id}`;
            
            const marker = L.marker([brf.latitude, brf.longitude], {
              icon: customIcon, 
              title: title,
              brfId: brf.core_id, 
              brfName: title 
            })
            .bindPopup(
              `<div class="custom-popup brf-popup">
                 <h4>${title}</h4>
                 ${starsHtmlBlock} <!-- Insert the generated HTML block -->
                 ${brf.address ? `<p class="address">${brf.address}</p>` : ''}
                 ${brf.distance ? `<p class="distance">Avstånd: ${brf.distance.toFixed(2)} km</p>` : ''}
                 <a href="/brf/${brf.core_id}/${slugify(title)}" id="${buttonId}" class="nearby-brf-button">Visa förening</a>
               </div>`
            );
            
            // Add a direct click handler to the marker itself
            marker.on('popupopen', function() {
              console.log('Popup opened for marker:', this.options.title);
              
              // Use setTimeout to ensure the DOM is ready
              setTimeout(() => {
                const button = document.getElementById(buttonId);
                if (button) {
                  console.log('Found button with ID:', buttonId);
                  button.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const brfId = marker.options.brfId;
                    const name = marker.options.brfName;
                    const slug = slugify(name);
                    
                    console.log(`Button clicked! Navigating to: /brf/${brfId}/${slug}`);
                    
                    // Try both navigation methods
                    try {
                      goto(`/brf/${brfId}/${slug}`);
                    } catch (error) {
                      console.error('Error with goto navigation:', error);
                      window.location.href = `/brf/${brfId}/${slug}`;
                    }
                    
                    return false; // Prevent any default behavior
                  });
                } else {
                  console.warn(`Button with ID ${buttonId} not found in DOM`);
                }
              }, 100); // Small delay to ensure popup is fully rendered
            });
            
            markers.push(marker);
          } else {
            console.warn("Skipping nearby BRF due to missing data:", brf);
          }
        });

        console.log(`Created ${markers.length} markers.`); // Log how many markers were actually created

        if (markers.length > 0) {
            nearbyMarkersLayer = L.layerGroup(markers).addTo(map);
            console.log('Added nearby markers layer to map.'); // Confirm layer addition
        } else {
            console.warn('No valid markers were created from the nearby BRF data.');
        }
      } else if (nearbyBrfs && nearbyBrfs.length === 0) {
          console.log("No nearby BRFs found after filtering.");
          if (nearbyMarkersLayer) {
              map.removeLayer(nearbyMarkersLayer);
              nearbyMarkersLayer = null;
          }
      } else {
          console.warn("Map object not ready or nearbyBrfs array is invalid when trying to add markers.");
      }
    } catch (error) {
      // Log the detailed error from the fetch block or JSON parsing
      console.error("Error fetching or displaying nearby BRFs:", error);
    }
  }

  // Function to initialize or reinitialize the map
  function initializeMap() {
    try {
      // Clean up existing map if it exists
      if (map) {
        map.remove();
        map = null;
        marker = null;
        nearbyMarkersLayer = null;
      }
      
      if (window.L) {
        const L = window.L;
        const coords = getCoordinates();
        
        if (coords && mapElement) {
          // Create map with smooth zoom
          map = L.map(mapElement, {
            zoomAnimation: true,
            fadeAnimation: true,
            markerZoomAnimation: true,
            zoom: 16,
            zIndexOffset: -1000 // Add this to lower the map's base z-index
          }).setView(
            [coords.latitude, coords.longitude], 
            16
          );
          
          // Add a more modern and clean map style with Safari compatibility fixes
          const stadiaLayer = L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png', {
            maxZoom: 20,
            attribution: '',
            crossOrigin: "anonymous",
            zIndexOffset: -1000, // Add this to lower the tile layer's z-index
            errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
          });
          
          // Add fallback layer for Safari in case Stadia has issues
          const osmFallback = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '',
            crossOrigin: "anonymous",
            zIndexOffset: -1000 // Add this to lower the fallback layer's z-index
          });
          
          // Try to add Stadia layer first, with OSM as fallback
          stadiaLayer.on('tileerror', function(error) {
            console.warn('Stadia Maps tile error, falling back to OpenStreetMap', error);
            if (!map.hasLayer(osmFallback)) {
              map.removeLayer(stadiaLayer);
              osmFallback.addTo(map);
            }
          });
          
          // Add the primary layer to the map
          stadiaLayer.addTo(map);
          
          // Add marker for the property with custom icon
          marker = L.marker([coords.latitude, coords.longitude], {
            icon: customIcon, // Use the defined custom icon
            title: $selectedItem.name || 'Fastigheten',
            zIndexOffset: 1000 // Make main marker appear on top
          })
            .addTo(map)
            .bindPopup(
              `<div class="custom-popup brf-popup">
                <h4>${$selectedItem.brf_name || $selectedItem.name || 'Fastigheten'}</h4>
                ${coords.address ? `<p>${coords.address}</p>` : ''}
              </div>`,
              { 
                closeButton: true,
                className: 'custom-popup-class',
                maxWidth: 300,
                minWidth: 200,
                autoPan: true
              }
            )
            .openPopup();
          
          // Add zoom controls
          L.control.zoom({
            position: 'bottomright'
          }).addTo(map);
          
          // Add fullscreen control
          if (L.control.fullscreen) {
            L.control.fullscreen({
              position: 'topright',
              title: 'Visa i helskärm',
              titleCancel: 'Avsluta helskärm',
              content: '<i class="fas fa-expand"></i>'
            }).addTo(map);
          }
          
          // Improve map responsiveness
          window.addEventListener('resize', () => {
            if (map) map.invalidateSize();
          });
          
          // Add a subtle circle to highlight the area
          L.circle([coords.latitude, coords.longitude], {
            color: '#ec776a',
            fillColor: '#ec776a',
            fillOpacity: 0.1,
            radius: 150,
            weight: 1
          }).addTo(map);

          // --- Fetch nearby BRFs, passing the current core_id for filtering ---
          if ($selectedItem && $selectedItem.core_id) {
             fetchAndDisplayNearbyBrfs(coords.latitude, coords.longitude, $selectedItem.core_id);
          } else {
             console.warn("Cannot fetch nearby BRFs: Missing coordinates or core_id for the selected item.");
          }
          // --------------------------------------------------------------------
        }
      } else {
        console.error("Leaflet library not loaded");
      }
    } catch (error) {
      console.error("Error loading map:", error);
    }
  }

  onMount(() => {
    // We don't need to initialize here anymore as the reactive statement will handle it
    // The initialization will happen automatically when selectedItem is available
    
    // Cleanup on component destruction
    return () => {
      if (map) {
        map.remove();
        map = null; // Help garbage collection
      }
    };
  });
</script>


{#if getCoordinates()}
  <div class="map-container">
    <div bind:this={mapElement} class="map"></div>
  </div>
{/if}


<style>
  .map-container {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    /* Ensure the container fills the column */
    display: flex; 
    flex-direction: column;
    height: 100%; 
    z-index: 4; /* Changed to 4 as requested */
    position: relative; /* Add position relative to ensure z-index works */
  }
  
  .map-container:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
  
  .map {
    width: 100%;
    /* Make the map div fill its container's height */
    height: 100%; 
    min-height: 300px; /* Optional: set a minimum height */
    border-radius: 8px;
    flex-grow: 1; /* Allow map to grow within the flex column */
  }
  
  /* These styles will be applied globally to make the popup look better */
  :global(.custom-popup-class) {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 3px 14px rgba(0,0,0,0.2);
  }
  
  :global(.custom-popup-class .leaflet-popup-content-wrapper) {
    border-radius: 8px;
    padding: 0;
    overflow: hidden;
  }
  
  :global(.custom-popup-class .leaflet-popup-content) {
    margin: 0;
  }
  
  :global(.custom-popup-class .custom-popup) {
    padding: 16px;
  }
  
  :global(.custom-popup-class .custom-popup p) {
    margin: 5px 0;
    font-size: 14px;
  }
  
  :global(.leaflet-container) {
    font-family: inherit;
    border-radius: 8px;
    background-color: white; /* Set background to white */
  }

  /* --- Specific overrides for the "Visa detaljer" button --- */
  :global(.leaflet-popup-content .nearby-brf-button) {
    background-color: #ec776a;
    color: white !important; /* Force override with !important */
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    margin-top: 8px;
    display: inline-block;
    text-align: center;
    transition: background-color 0.2s ease;
    text-decoration: none !important; /* Force override with !important */
  }

  :global(.leaflet-popup-content .nearby-brf-button:hover) {
    background-color: #d9685a;
    color: white !important; /* Force override with !important */
    text-decoration: none !important; /* Force override with !important */
  }

  /* Override ALL link styles within Leaflet popups */
  :global(.leaflet-popup-content a) {
    color: inherit; /* Reset inherited colors */
    text-decoration: none;
  }
  
  /* Override any Leaflet container link styles */
  :global(.leaflet-container a.nearby-brf-button) {
    color: white !important;
    text-decoration: none !important;
  }

  /* Style the attribution link */
  :global(.leaflet-container a) {
    color: white; /* Change link color to a medium grey */
  }

  /* Add styles for the nearby popup button */
  :global(.custom-popup.brf-popup h4) {
    margin: 0 0 4px 0; /* Reduced bottom margin */
    color: #333;
    font-size: 15px !important;
    font-weight: 600 !important;
  }

  /* Styles for address and distance */
   :global(.brf-popup p.address) {
    margin: 0 0 4px 0; /* Tighten spacing */
    font-size: 13px;
    color: #555;
    line-height: 1.3;
  }
   :global(.brf-popup p.distance) {
    margin: 0 0 8px 0; /* Space before button */
    font-size: 12px;
    color: #777;
  }

  /* --- Add Global Styles for Star Ratings --- */
  :global(.rating-stars) { /* Container for text fallback */
    margin-bottom: 6px;
    font-size: 13px;
    color: #777;
  }

  :global(.stars) {
    display: inline-flex; /* Align stars horizontally */
    align-items: center;
    height: 16px; /* Adjust height as needed */
    margin-bottom: 6px; /* Space below stars */
  }

  :global(.star) {
    width: 16px; /* Width of one star */
    height: 16px; /* Height of one star */
    margin-right: 2px; /* Space between stars */
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="lightgray"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>'); /* Empty star (grey) */
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  :global(.star.filled) {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ffc107"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>'); /* Filled star (gold) */
  }

  :global(.star.half-filled) {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><defs><linearGradient id="half"><stop offset="50%" stop-color="%23ffc107"/><stop offset="50%" stop-color="lightgray"/></linearGradient></defs><path fill="url(%23half)" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>'); /* Half-filled star */
  }
  /* ----------------------------------------- */
</style> 